/**
 * Product Upsell Widget
 * Handles toggle interactions and bundle cart additions
 */

(function () {
  // Gate noisy debug logging behind either:
  // - `window.DYC_DEBUG = true`
  // - `?dyc_debug=1`
  const DYC_DEBUG =
    (typeof window !== 'undefined' && (window.DYC_DEBUG === true || window.DYC_DEBUG === 'true')) ||
    (typeof window !== 'undefined' && /(?:\\?|&)dyc_debug=1\\b/.test(window.location.search));

  const console = DYC_DEBUG
    ? window.console
    : {
        log() {},
        info() {},
        debug() {},
        warn() {},
        // Keep errors visible even when debug is off
        error:
          typeof window !== 'undefined' && window.console && window.console.error
            ? window.console.error.bind(window.console)
            : function () {},
      };

if (!customElements.get('product-upsell-widget')) {
  customElements.define(
    'product-upsell-widget',
    class ProductUpsellWidget extends HTMLElement {
      constructor() {
        super();
        
        this.productId = this.dataset.productId;
        this.currentVariantId = this.dataset.variantId;
        this.sectionId = this.dataset.sectionId;
        this.selectedUpsells = new Map();
        this.selectedAccessories = new Set();
        
        // Get customization settings
        this.linkText = this.dataset.linkText || 'View Product';
        this.linkColor = this.dataset.linkColor || '#495057';
        this.toggleColor = this.dataset.toggleColor || '#6DC3F7';
        
        console.log('🎁 Upsell Widget initialized for product:', this.productId, 'section:', this.sectionId);
        
        // Load variant upsell data
        this.variantUpsellData = this.loadVariantUpsellData();
        
        // Get references to elements
        this.toggleInputs = this.querySelectorAll('.upsell-toggle-input');
        
        // Try multiple ways to find the product form
        this.productForm = document.querySelector(`product-form[data-section="${this.sectionId}"]`);
        
        if (!this.productForm) {
          // Check for MCC product form (OEM products)
          this.productForm = document.querySelector(`mcc-product-form[data-section="${this.sectionId}"]`);
          if (this.productForm) {
            console.log('🎨 Using MCC product-form (OEM)');
          }
        }
        
        if (!this.productForm) {
          // Check for car kit builder form
          this.productForm = document.querySelector('product-form[data-section^="ckb-"]');
          if (this.productForm) {
            console.log('🚗 Using car kit builder product-form');
          }
        }
        
        if (!this.productForm) {
          // Fallback: find any product-form or mcc-product-form on the page
          this.productForm = document.querySelector('product-form, mcc-product-form');
          console.log('🎁 Using fallback product-form');
        }
        
        this.cartDrawer = document.getElementById('Cart-Drawer');
        
        console.log('🎁 Upsell Widget for product:', this.productId);
        console.log('🎁 Section ID:', this.sectionId);
        console.log('🎁 Found toggles:', this.toggleInputs.length);
        console.log('🎁 Product form:', this.productForm ? 'found' : 'NOT FOUND');
        console.log('🎁 Has variant data:', !!this.variantUpsellData);
        
        if (this.variantUpsellData) {
          console.log('🎁 Variant data has', Object.keys(this.variantUpsellData.variantUpsells).length, 'variants');
        }
        
        // Check if car kit builder exists on this page
        this.carKitBuilder = document.querySelector('[id^="car-kit-builder-"]');
        this.carKitSummary = this.carKitBuilder ? this.carKitBuilder.querySelector('.ckb-animated-summary-section') : null;
        
        if (this.carKitBuilder && this.carKitSummary) {
          console.log('🎁 Car Kit Builder detected, hiding widget until summary');
          
          // Hide widget initially
          this.style.display = 'none';
          
          // Watch for summary to become visible
          const summaryObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                if (this.carKitSummary.classList.contains('summary-visible')) {
                  console.log('🎁 Car Kit summary visible, showing upsell widget');
                  this.style.display = '';
                  
                  // Re-find the product form (it was created dynamically in the summary)
                  this.productForm = document.querySelector('product-form[data-section^="ckb-"]');
                  
                  // If not found, try mcc-product-form
                  if (!this.productForm) {
                    this.productForm = document.querySelector('mcc-product-form');
                    if (this.productForm) {
                      console.log('🎨 Re-queried and found mcc-product-form');
                    }
                  }
                  
                  console.log('🚗 Re-queried product form:', this.productForm ? 'found' : 'NOT FOUND');
                  
                  // Re-attach form submit listener now that form exists
                  if (this.productForm) {
                    this.initFormSubmitListener();
                    console.log('🚗 Re-attached form submit listener to car kit form');
                  }
                  
                  summaryObserver.disconnect(); // Stop observing once shown
                }
              }
            });
          });
          
          summaryObserver.observe(this.carKitSummary, {
            attributes: true,
            attributeFilter: ['class']
          });
        } else {
          console.log('🎁 No car kit builder, showing widget normally');
        }
        
        // Delay initialization slightly to ensure DOM is ready
        setTimeout(() => {
          // Initialize toggle listeners
          this.initToggleListeners();
          
          // Listen for variant changes
          this.initVariantChangeListener();
          
          // Listen for main product form submission
          this.initFormSubmitListener();

          // Listen for accessories selection changes (ALT accessories drawer + any other sources)
          this.initAccessoriesListener();

          // Keep main ATC text in sync with quantity changes
          this.initQuantityListener();
          
          console.log('🎁 Upsell widget initialization complete for product:', this.productId);
        }, 100);
        
        // Listen for cart updates from other sources (when items are added/removed elsewhere)
        if (window.subscribe && window.PUB_SUB_EVENTS) {
          this.cartUpdateUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, async (event) => {
            // Only refresh if the update didn't come from us (avoid double refresh)
            if (event.detail && event.detail.source !== 'upsell-widget') {
              console.log('🔄 Cart updated from another source, refreshing drawer...');
              
              // Wait a moment for discounts to calculate
              await new Promise(resolve => setTimeout(resolve, 200));
              
              // Fetch fresh cart data with cache-busting
              const cartTimestamp = Date.now();
              const cartResponse = await fetch(`/cart.js?t=${cartTimestamp}`, {
                cache: 'no-store'
              });
              const cartData = await cartResponse.json();
              
              // Refresh cart drawer if it's open
              if (this.cartDrawer && this.cartDrawer.hasAttribute('open')) {
                const sectionsToRender = this.getSectionsToRender();
                await this.fetchAndUpdateSections(sectionsToRender, cartData);
              }
            }
          });
        }
      }
      
      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }
      
      /**
       * Load variant upsell data from the JSON script tag
       */
      loadVariantUpsellData() {
        try {
          const dataElement = document.getElementById(`upsell-variant-data-${this.productId}`);
          if (dataElement) {
            return JSON.parse(dataElement.textContent);
          }
        } catch (error) {
          console.error('Error loading variant upsell data:', error);
        }
        return null;
      }
      
      /**
       * Initialize toggle switch event listeners
       */
      initToggleListeners() {
        this.toggleInputs.forEach(input => {
          input.addEventListener('change', (e) => {
            const variantId = e.target.dataset.variantId;
            const productId = e.target.dataset.productId;
            const productTitle = e.target.dataset.productTitle;
            
            if (e.target.checked) {
              this.selectedUpsells.set(variantId, {
                variantId: variantId,
                productId: productId,
                title: productTitle
              });
            } else {
              this.selectedUpsells.delete(variantId);
            }
            
            console.log('Selected upsells:', Array.from(this.selectedUpsells.values()));
            
            // Update button text to reflect selection count
            this.updateAddToCartButtonText();
          });
        });
      }
      
      /**
       * Update the Add to Cart button to show: qty | action | price
       */
      updateAddToCartButtonText() {
        const mainQty = this.getPrimaryQty();
        const upsellsCount = this.selectedUpsells ? this.selectedUpsells.size : 0;
        const accessoriesCount = this.selectedAccessories ? this.selectedAccessories.size : 0;

        const extrasCount = upsellsCount + accessoriesCount;
        const totalItems = mainQty + extrasCount;

        const scope = this.getScopeEl();
        const formId = this.getMainFormId();

        // Calculate total price
        const totalPriceCents = this.calculateTotalPrice();
        const formattedPrice = this.formatMoney(totalPriceCents);

        console.log('🎁 updateAddToCartButtonText:', { 
          scope, formId, mainQty, upsellsCount, accessoriesCount, 
          totalItems, totalPriceCents, formattedPrice 
        });

        // Prefer the shared enhancer so variant/qty changes elsewhere stay in sync.
        if (window.DYCEnhancedATC) {
          const mainPriceCents = this.getMainPriceCents();
          const extrasCents = Math.max(0, totalPriceCents - mainPriceCents * mainQty);

          window.DYCEnhancedATC.setExtras(this.sectionId, {
            count: extrasCount,
            cents: extrasCents,
          });
          window.DYCEnhancedATC.update(this.sectionId);

          console.log(`🎁 Updated Enhanced ATC via DYCEnhancedATC: items=${totalItems}, price=${formattedPrice}, extrasCount=${extrasCount}, extrasCents=${extrasCents}`);
          return;
        }

        // Fallback (should be rare): do nothing if the shared enhancer isn't available.
        console.warn('🎁 DYCEnhancedATC not found; skipping enhanced ATC update');
      }

      /**
       * Calculate total price of main product + upsells + accessories
       */
      calculateTotalPrice() {
        const mainQty = this.getPrimaryQty();
        let totalCents = 0;

        const mainPriceCents = this.getMainPriceCents();
        totalCents += mainPriceCents * mainQty;

        // Add upsell prices
        if (this.selectedUpsells && this.selectedUpsells.size > 0) {
          this.selectedUpsells.forEach((upsell, variantId) => {
            const toggleInput = this.querySelector(`.upsell-toggle-input[data-variant-id="${variantId}"]`);
            if (toggleInput) {
              const productItem = toggleInput.closest('.upsell-product-item');
              const priceEl = productItem?.querySelector('.upsell-product-price .final-price, .upsell-product-price');
              if (priceEl) {
                const priceText = priceEl.textContent || '';
                const match = priceText.match(/[\d,]+\.?\d*/);
                if (match) {
                  const priceValue = parseFloat(match[0].replace(/,/g, ''));
                  if (!isNaN(priceValue)) {
                    totalCents += Math.round(priceValue * 100);
                    console.log('🎁 Added upsell price:', priceValue, 'for variant', variantId);
                  }
                }
              }
            }
          });
        }

        // Add accessory prices (from stored data if available)
        if (this.selectedAccessories && this.selectedAccessories.size > 0 && this.accessoryPrices) {
          this.selectedAccessories.forEach((variantId) => {
            const priceCents = this.accessoryPrices.get(String(variantId));
            if (priceCents) {
              totalCents += priceCents;
              console.log('🎁 Added accessory price:', priceCents / 100, 'for variant', variantId);
            }
          });
        }

        return totalCents;
      }

      /**
       * Get main product price (cents) for this section.
       * Used both for total calc and for deriving extras-only cents.
       */
      getMainPriceCents() {
        const scope = this.getScopeEl();

        const priceSelectors = [
          `#Price-${this.sectionId} .price-item--sale`,
          `#Price-${this.sectionId} .price-item--regular`,
          `#Price-${this.sectionId}`,
          '.price--large .price-item--sale',
          '.price--large .price-item--regular',
          '.product-price .price-item--sale',
          '.product-price .price-item--regular',
          '[data-product-price]',
        ];

        for (const selector of priceSelectors) {
          const priceEl = scope.querySelector(selector);
          if (!priceEl) continue;
          const priceText = priceEl.textContent || '';
          const match = priceText.match(/[\d,]+\.?\d*/);
          if (!match) continue;
          const priceValue = parseFloat(match[0].replace(/,/g, ''));
          if (!isNaN(priceValue) && priceValue > 0) {
            const cents = Math.round(priceValue * 100);
            return cents;
          }
        }

        return 0;
      }

      /**
       * ALT-safe: determine the main product form id.
       * - Primary: read from the actual form element inside product-form/mcc-product-form.
       * - Fallback: expected Shopify id pattern used by this theme.
       */
      getScopeEl() {
        // Best scope: the section wrapper for this specific PDP instance
        const byId = document.getElementById(`MainProduct-${this.sectionId}`);
        if (byId) return byId;

        // Fallback: closest section container
        return this.closest('.section-main-product') || this.closest('.shopify-section') || document;
      }

      getMainFormId() {
        const scope = this.getScopeEl();

        // Try the real form inside this specific section
        const formInScope =
          scope.querySelector('product-form form[id], mcc-product-form form[id]') ||
          scope.querySelector('form[id^="product-form-"]');

        const id = formInScope?.getAttribute('id');
        return id || `product-form-${this.sectionId}`;
      }

      /**
       * ALT-safe: find the primary Add to Cart button.
       * Supports:
       * - Standard templates: button inside <product-form>
       * - ALT qty_atc_row: external submit button with [form="product-form-..."]
       */
      getPrimaryAddToCartButton() {
        const scope = this.getScopeEl();
        const formId = this.getMainFormId();

        // 1) ALT: external submit button linked to form id (inside this section)
        const external =
          scope.querySelector(`button.qty-atc-row__atc[form="${formId}"]`) ||
          scope.querySelector(`button[type="submit"][form="${formId}"]`);
        if (external) return external;

        // 2) Standard: submit button inside the form itself
        const formEl = scope.querySelector(`form#${CSS.escape(formId)}`);
        const insideForm =
          formEl?.querySelector('.add-cart-button, .fd-add-cart-button, .ckb-add-cart-button') ||
          formEl?.querySelector('button[type="submit"][name="add"]') ||
          formEl?.querySelector('button[type="submit"]');
        if (insideForm) return insideForm;

        // 3) Fallback: inside product-form component in this section
        const pf = scope.querySelector('product-form, mcc-product-form');
        const insidePf =
          pf?.querySelector('.add-cart-button, .fd-add-cart-button, .ckb-add-cart-button') ||
          pf?.querySelector('button[type="submit"][name="add"]') ||
          pf?.querySelector('button[type="submit"]');
        if (insidePf) return insidePf;

        return null;
      }

      /**
       * ALT-safe: read the main quantity value (defaults to 1).
       */
      getPrimaryQty() {
        const scope = this.getScopeEl();
        const formId = this.getMainFormId();

        const qtyInput =
          scope.querySelector(`input[name="quantity"][form="${formId}"]`) ||
          scope.querySelector(`#Qty-${CSS.escape(this.sectionId)}`) ||
          scope.querySelector(`#Quantity-${CSS.escape(this.sectionId)}`) ||
          scope.querySelector('product-form input[name="quantity"], mcc-product-form input[name="quantity"]') ||
          (this.productForm ? this.productForm.querySelector('input[name="quantity"]') : null);

        const qty = Math.max(1, parseInt(qtyInput?.value || '1', 10));
        return qty;
      }

      /**
       * Keep button text in sync with quantity changes.
       */
      initQuantityListener() {
        // Avoid double attach
        if (this._qtyListenerAttached) return;
        this._qtyListenerAttached = true;

        const scope = this.getScopeEl();
        const formId = this.getMainFormId();

        const handler = () => this.updateAddToCartButtonText();

        // Quantity may live inside form or outside with [form="..."]
        const attach = (input) => {
          if (!input || input.dataset.dycQtyListenerAttached) return;
          input.dataset.dycQtyListenerAttached = 'true';
          input.addEventListener('input', handler);
          input.addEventListener('change', handler);
        };

        attach(scope.querySelector(`input[name="quantity"][form="${formId}"]`));
        attach(scope.querySelector(`#Qty-${CSS.escape(this.sectionId)}`));
        attach(scope.querySelector(`form#${CSS.escape(formId)} input[name="quantity"]`));
        if (this.productForm) attach(this.productForm.querySelector('input[name="quantity"]'));
      }

      /**
       * Listen for accessories selection changes dispatched from the ALT accessories drawer.
       */
      initAccessoriesListener() {
        if (this._accessoriesListenerAttached) return;
        this._accessoriesListenerAttached = true;

        // Initialize price storage for accessories
        this.accessoryPrices = this.accessoryPrices || new Map();

        document.addEventListener('dyc:accessories-changed', (event) => {
          const variantIds = event?.detail?.variantIds || [];
          const prices = event?.detail?.prices || {}; // { variantId: priceCents }
          
          this.selectedAccessories = new Set(variantIds.map(String));
          
          // Store prices if provided
          if (prices && typeof prices === 'object') {
            Object.entries(prices).forEach(([variantId, priceCents]) => {
              this.accessoryPrices.set(String(variantId), priceCents);
            });
          }
          
          this.updateAddToCartButtonText();
        });

        document.addEventListener('dyc:accessories-clear', () => {
          this.selectedAccessories.clear();
          this.accessoryPrices.clear();
          this.updateAddToCartButtonText();
        });
      }
      
      /**
       * Listen for variant changes on the main product
       */
      initVariantChangeListener() {
        if (!this.productForm) {
          console.warn('🎁 No product form found - variant changes will not work');
          return;
        }
        
        console.log('🎧 Setting up variant change listeners for product:', this.productId);
        
        // Subscribe to variant change events
        document.addEventListener('variant:change', (event) => {
          console.log('🔔 variant:change event received:', event.detail);
          if (event.detail && event.detail.variant) {
            this.handleVariantChange(event.detail.variant);
          }
        });
        
        // Also listen to the variant input directly
        // For car kit builders, look for the specific car kit variant input
        let variantInput = this.productForm.querySelector('[id^="ckb-variant-id-animated-"]');
        
        if (!variantInput) {
          // Fallback to standard variant input
          variantInput = this.productForm.querySelector('[name="id"]');
        }
        
        console.log('🎯 Variant input found:', variantInput ? 'yes' : 'no', 'current value:', variantInput?.value, 'id:', variantInput?.id);
        
        if (variantInput) {
          // Watch for input/change events
          ['input', 'change'].forEach(eventType => {
            variantInput.addEventListener(eventType, (e) => {
              console.log(`🔔 ${eventType} event on variant input for product ${this.productId}:`, e.target.value);
              if (this.variantUpsellData) {
                this.updateWidgetForVariant(e.target.value);
              }
            });
          });
          
          console.log('🎁 Variant input listeners attached');
        }
        
        // Listen to all option changes (radio buttons, selects) - global for all products
        document.addEventListener('change', (e) => {
          if (e.target.name && e.target.name.includes('options[')) {
            const form = e.target.closest('form');
            const productForm = form?.closest('product-form');
            
            // Only update if this is for our product
            if (productForm && productForm === this.productForm) {
              console.log('🔔 Option changed for product', this.productId, ':', e.target.name, '=', e.target.value);
              // Wait a bit for the variant ID to update
              setTimeout(() => {
                const currentVariantId = variantInput?.value;
                if (currentVariantId && this.variantUpsellData) {
                  console.log('🔄 Updating widget after option change, variant:', currentVariantId);
                  this.updateWidgetForVariant(currentVariantId);
                }
              }, 150);
            }
          }
        }, true); // Use capture to catch early
        
        // Combination approach: MutationObserver + polling
        // Store reference to this widget for closure
        const widget = this;
        const formEl = this.productForm;
        
        if (variantInput) {
          let lastVariantId = variantInput.value;
          console.log('🎁 Setting up dual detection for product', this.productId, 'initial variant:', lastVariantId);
          
          // Method 1: MutationObserver for attribute changes
          const observer = new MutationObserver((mutations) => {
            console.log('🔍 MutationObserver fired for product', widget.productId, 'mutations:', mutations.length);
            
            const currentVariantId = variantInput.value || variantInput.getAttribute('value');
            console.log('🔍 Current vs Last:', currentVariantId, 'vs', lastVariantId);
            
            if (currentVariantId && currentVariantId !== lastVariantId && widget.variantUpsellData) {
              console.log('🔔 MutationObserver detected variant change for product', widget.productId, ':', lastVariantId, '→', currentVariantId);
              lastVariantId = currentVariantId;
              widget.updateWidgetForVariant(currentVariantId);
            }
          });
          
          observer.observe(variantInput, {
            attributes: true,
            attributeFilter: ['value']
          });
          
          console.log('🎁 MutationObserver attached to:', variantInput, 'watching attribute:', 'value');

          // Method 2: Polling as fallback (checks property AND attribute)
          // ANDROID OPTIMIZATION: Use longer intervals on mobile to reduce CPU load
          const isMobile = window.matchMedia('(max-width: 1024px)').matches ||
                           ('ontouchstart' in window) ||
                           (navigator.maxTouchPoints > 0);

          // Skip polling entirely on mobile - MutationObserver + event listeners are sufficient
          // This prevents the constant 300ms DOM queries that cause Android performance issues
          if (!isMobile) {
            let pollCount = 0;
            setInterval(() => {
              pollCount++;

              // Re-query fresh each time - check for car kit variant input first
              let freshInput = formEl ? formEl.querySelector('[id^="ckb-variant-id-animated-"]') : null;
              if (!freshInput) {
                freshInput = formEl ? formEl.querySelector('[name="id"]') : null;
              }
              if (!freshInput || !widget.variantUpsellData) return;

              const currentVariantId = freshInput.value || freshInput.getAttribute('value');

              // Log every 20 polls for debugging with more detail
              if (pollCount % 20 === 0) {
                console.log(`🔍 Poll #${pollCount} for product ${widget.productId}:`);
                console.log('  - Input element:', freshInput);
                console.log('  - .value:', freshInput.value);
                console.log('  - getAttribute("value"):', freshInput.getAttribute('value'));
                console.log('  - Computed:', currentVariantId);
                console.log('  - Last known:', lastVariantId);
                console.log('  - Match?', currentVariantId === lastVariantId);
              }

              if (currentVariantId && currentVariantId !== lastVariantId) {
                console.log('🔔 Polling detected variant change for product', widget.productId, ':', lastVariantId, '→', currentVariantId);
                lastVariantId = currentVariantId;
                widget.updateWidgetForVariant(currentVariantId);
              }
            }, 1000); // Check every 1000ms on desktop only
          }

          console.log('🎁 Dual detection (Observer + Polling) started for product', this.productId);
          
          // Method 4: Intercept FormData to detect what variant Size Selector is using
          // This catches the FormData override that Size Selector uses
          const originalFormData = window.FormData;
          const upsellWidget = widget;
          
          window.FormData = function(form) {
            const formData = new originalFormData(form);
            
            // If this is our product's form and it's a cart form
            if (form && form.matches && form.matches('form[action*="/cart/add"]')) {
              const formProductForm = form.closest('product-form');
              
              // Check if this is our product's form
              if (formProductForm === formEl) {
                const submittedVariantId = formData.get('id');
                
                // If the variant being submitted is different from what we're showing
                if (submittedVariantId && submittedVariantId !== lastVariantId && upsellWidget.variantUpsellData) {
                  console.log('🔔 FormData intercept detected variant for product', upsellWidget.productId, ':', lastVariantId, '→', submittedVariantId);
                  lastVariantId = submittedVariantId;
                  // Update immediately before form submission
                  upsellWidget.updateWidgetForVariant(submittedVariantId);
                }
              }
            }
            
            return formData;
          };
          
          console.log('🎁 FormData intercept installed for product', this.productId);
          
          // Method 5: Watch price changes (Size Selector updates price)
          const priceElement = document.getElementById(`Price-${widget.sectionId}`);
          if (priceElement) {
            const priceObserver = new MutationObserver(() => {
              console.log('🔔 Price changed, checking variant input...');
              
              // Check input after a delay to let Size Selector finish
              setTimeout(() => {
                let freshInput = formEl ? formEl.querySelector('[id^="ckb-variant-id-animated-"]') : null;
                if (!freshInput) {
                  freshInput = formEl ? formEl.querySelector('[name="id"]') : null;
                }
                if (freshInput) {
                  const currentVariantId = freshInput.value || freshInput.getAttribute('value');
                  if (currentVariantId && currentVariantId !== lastVariantId && widget.variantUpsellData) {
                    console.log('🔔 Price observer detected variant change for product', widget.productId, ':', lastVariantId, '→', currentVariantId);
                    lastVariantId = currentVariantId;
                    widget.updateWidgetForVariant(currentVariantId);
                  }
                }
              }, 100);
            });
            
            priceObserver.observe(priceElement, {
              characterData: true,
              childList: true,
              subtree: true
            });
            
            console.log('🎁 Price observer watching for product', widget.productId);
          }
          
          // Method 6: Listen for Size Selector's syncToForm by intercepting console.log carefully
          // Only install once globally, but filter by product
          if (!window.__upsellConsoleInterceptInstalled) {
            window.__upsellConsoleInterceptInstalled = true;
            window.__upsellWidgets = window.__upsellWidgets || new Map();
            
            const originalLog = console.log;
            console.log = function(...args) {
              originalLog.apply(console, args);
              
              // Check if this is a Size Selector log about using a variant
              if (args[0] && typeof args[0] === 'string' && args[0].includes('✅ Size Selector: Using variant ID:')) {
                const logStr = args.join(' ');
                const variantMatch = logStr.match(/Using variant ID: (\d+)/);
                const skuMatch = logStr.match(/SKU: ([A-Z0-9-]+)/);
                
                if (variantMatch) {
                  const detectedVariantId = variantMatch[1];
                  const sku = skuMatch ? skuMatch[1] : null;
                  
                  // Notify all widgets, they'll check if it's their product
                  window.__upsellWidgets.forEach((widgetInstance, productId) => {
                    if (widgetInstance.variantUpsellData && widgetInstance.variantUpsellData.variantUpsells[detectedVariantId]) {
                      // This variant belongs to this product
                      widgetInstance.handleSizeSelectorVariantChange(detectedVariantId);
                    }
                  });
                }
              }
            };
            
            originalLog('🎁 Global console intercept installed for Size Selector detection');
          }
          
          // Register this widget
          window.__upsellWidgets = window.__upsellWidgets || new Map();
          window.__upsellWidgets.set(this.productId, this);
          console.log('🎁 Widget registered for console intercept:', this.productId);
          
          // Method 3: Watch SKU changes (Size Selector updates SKU element)
          const skuElement = document.querySelector(`[id^="Sku-"]`);
          if (skuElement) {
            let lastSKU = skuElement.textContent;
            
            const skuObserver = new MutationObserver(() => {
              const currentSKU = skuElement.textContent;
              
              // SKU changed, try to find matching variant
              if (currentSKU !== lastSKU) {
                console.log('🔔 SKU changed:', lastSKU, '→', currentSKU);
                lastSKU = currentSKU;
                
                // Parse SKU to find variant (e.g., "SKU: ANTHIAS-A-PERM" -> match with variant)
                const skuMatch = currentSKU.match(/([A-Z0-9-]+)$/);
                if (skuMatch && widget.variantUpsellData) {
                  const sku = skuMatch[1];
                  console.log('🔍 Looking for variant with SKU:', sku);
                  
                  // Search through all variants to find one with this SKU
                  const allVariants = Object.values(widget.variantUpsellData.variantUpsells);
                  // Note: We don't have SKU in our data, so use a delay and check input
                  setTimeout(() => {
                    let freshInput = formEl ? formEl.querySelector('[id^="ckb-variant-id-animated-"]') : null;
                    if (!freshInput) {
                      freshInput = formEl ? formEl.querySelector('[name="id"]') : null;
                    }
                    if (freshInput) {
                      const currentVariantId = freshInput.value || freshInput.getAttribute('value');
                      if (currentVariantId && currentVariantId !== lastVariantId) {
                        console.log('🔔 SKU observer detected variant change for product', widget.productId, ':', lastVariantId, '→', currentVariantId);
                        lastVariantId = currentVariantId;
                        widget.updateWidgetForVariant(currentVariantId);
                      }
                    }
                  }, 250);
                }
              }
            });
            
            skuObserver.observe(skuElement, {
              characterData: true,
              childList: true,
              subtree: true
            });
            
            console.log('🎁 SKU observer watching:', skuElement, 'initial SKU:', lastSKU);
          } else {
            console.log('🎁 No SKU element found');
          }
        } else {
          console.warn('🎁 No variant input found, detection not started');
        }
      }
      
      /**
       * Handle variant change
       */
      handleVariantChange(variant) {
        if (!variant || !variant.id) return;
        console.log('🔄 Variant changed to:', variant.id);
        this.updateWidgetForVariant(variant.id);
      }
      
      /**
       * Handle Size Selector variant change (from console intercept)
       */
      handleSizeSelectorVariantChange(variantId) {
        console.log('🔔 Size Selector variant detected for product', this.productId, ':', variantId);
        this.updateWidgetForVariant(variantId);
      }
      
      /**
       * Update widget display for a specific variant
       */
      async updateWidgetForVariant(variantId) {
        console.log('🔄 updateWidgetForVariant called with:', variantId);
        
        if (!this.variantUpsellData) {
          console.warn('❌ No variant upsell data available');
          return;
        }
        
        this.currentVariantId = variantId;
        
        // Get upsell products for this variant
        const variantData = this.variantUpsellData.variantUpsells[variantId];
        console.log('📦 Variant data for', variantId, ':', variantData);
        
        if (!variantData) {
          console.warn('❌ No upsell data for variant:', variantId);
          console.log('Available variant IDs:', Object.keys(this.variantUpsellData.variantUpsells));
          console.log('⚠️ Hiding widget - no upsells for this variant');
          this.style.display = 'none';
          return;
        }
        
        // Combine all upsell products from all metafields (skip duplicates)
        const allUpsells = [];
        const seenIds = new Set();
        
        console.log('🔍 Checking upsell_list:', variantData.upsell_list);
        console.log('🔍 Checking upsell_list_1:', variantData.upsell_list_1);
        console.log('🔍 Checking variant_product_upsell_list:', variantData.variant_product_upsell_list);
        
        // Debug first product structure from any available list
        const firstProduct = variantData.upsell_list?.[0] || variantData.upsell_list_1?.[0] || variantData.variant_product_upsell_list?.[0];
        if (firstProduct) {
          console.log('🔬 First product detailed structure:', {
            id: firstProduct.id,
            title: firstProduct.title,
            variant_title: firstProduct.variant_title,
            variant_id: firstProduct.variant_id,
            total_variants: firstProduct.total_variants,
            all_variants: firstProduct.all_variants,
            variantsLength: firstProduct.variants?.length,
            hasMultipleVariants: firstProduct.total_variants > 1
          });
        }
        
        // Add from upsell_list
        if (variantData.upsell_list && Array.isArray(variantData.upsell_list)) {
          variantData.upsell_list.forEach(product => {
            if (product && !seenIds.has(product.id)) {
              allUpsells.push(product);
              seenIds.add(product.id);
            }
          });
        }
        
        // Add from upsell_list_1
        if (variantData.upsell_list_1 && Array.isArray(variantData.upsell_list_1)) {
          variantData.upsell_list_1.forEach(product => {
            if (product && !seenIds.has(product.id)) {
              allUpsells.push(product);
              seenIds.add(product.id);
            }
          });
        }
        
        // Add from variant_product_upsell_list
        if (variantData.variant_product_upsell_list && Array.isArray(variantData.variant_product_upsell_list)) {
          variantData.variant_product_upsell_list.forEach(product => {
            if (product && !seenIds.has(product.id)) {
              allUpsells.push(product);
              seenIds.add(product.id);
            }
          });
        }
        
        // If no variant upsells, use product upsells
        if (allUpsells.length === 0 && this.variantUpsellData.productUpsells) {
          console.log('📦 Using product-level upsells as fallback');
          if (Array.isArray(this.variantUpsellData.productUpsells)) {
            this.variantUpsellData.productUpsells.forEach(product => {
              if (product) allUpsells.push(product);
            });
          }
        }
        
        console.log('🎁 Found', allUpsells.length, 'upsell products for variant:', variantId);
        console.log('🎁 Products:', allUpsells);
        
        // Rebuild the widget UI
        this.rebuildWidget(allUpsells.slice(0, 3)); // Max 3 products
      }
      
      /**
       * Rebuild the widget with new products
       */
      rebuildWidget(products) {
        console.log('🔨 rebuildWidget called with', products?.length || 0, 'products');
        
        const productsList = this.querySelector('.upsell-products-list');
        if (!productsList) {
          console.warn('❌ Products list element not found');
          return;
        }
        
        // Save current selections to restore after rebuild
        const previousSelections = new Map(this.selectedUpsells);
        console.log('💾 Saving', previousSelections.size, 'previous selections');
        
        // If no products, hide widget
        if (!products || products.length === 0) {
          console.log('⚠️ No products to display, hiding widget');
          this.style.display = 'none';
          return;
        }
        
        // Show widget
        this.style.display = '';
        
        // Build HTML for products
        let html = '';
        products.forEach((product, index) => {
          if (!product) return;
          
          console.log(`🖼️ Product ${index + 1}:`, product.title, 'variant:', product.variant_title, 'image:', product.featured_image);
          
          // Get image - use featured_image which includes the full image URL
          const imageUrl = product.featured_image || '';
          
          // Use variant_id from data, or fallback to first variant or product id
          const variantId = product.variant_id || (product.variants && product.variants.length > 0 ? product.variants[0] : product.id);
          
          // Build display title: "Product Name | Variant Title" or just "Product Name"
          // Check if product has multiple variants (more than just default)
          const hasMultipleVariants = product.total_variants > 1;
          const hasValidVariantTitle = product.variant_title && product.variant_title !== 'Default Title';
          const displayTitle = (hasMultipleVariants && hasValidVariantTitle)
            ? `${product.title} | ${product.variant_title}`
            : product.title;
          
          console.log(`📝 Display title: "${displayTitle}", hasMultipleVariants: ${hasMultipleVariants}, total_variants: ${product.total_variants}, all_variants: ${product.all_variants?.length}, variant ID: ${variantId}`);
          
          html += `
            <div class="upsell-product-item" data-product-id="${product.id}">
              <div class="upsell-product-image">
                ${imageUrl ? `
                  <img 
                    src="${imageUrl}"
                    alt="${displayTitle}"
                    width="80"
                    height="80"
                    loading="lazy"
                  >
                ` : '<div class="upsell-product-placeholder"></div>'}
              </div>
              
              <div class="upsell-product-info">
                <p class="upsell-product-title">${displayTitle}</p>
                <p class="upsell-product-price">
                  ${product.price_varies ? '<span class="price-from">From</span>' : ''}
                  ${product.has_auto_discount && product.original_price > product.price ? `
                    <span class="discounted-prices">
                      <s class="original-price"><small>${this.formatMoney(product.original_price)}</small></s>
                      <b class="final-price"><small>${this.formatMoney(product.price)}</small></b>
                    </span>
                  ` : `
                    <span class="original-price">${this.formatMoney(product.price)}</span>
                  `}
                </p>
                <a href="${product.url}" class="upsell-learn-more">${this.linkText}</a>
              </div>
              
              <div class="upsell-product-toggle">
                <label class="toggle-switch">
                  <input 
                    type="checkbox" 
                    class="upsell-toggle-input"
                    data-product-id="${product.id}"
                    data-variant-id="${variantId}"
                    data-product-title="${product.title}"
                  >
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>
          `;
        });
        
        productsList.innerHTML = html;
        
        // Re-initialize toggle listeners
        this.toggleInputs = this.querySelectorAll('.upsell-toggle-input');
        this.initToggleListeners();
        
        // CLEAR old selections before restoring - prevents phantom selections from accumulating
        this.selectedUpsells.clear();
        
        // Restore previous selections ONLY if the same variants are still available in new product list
        this.toggleInputs.forEach(input => {
          const variantId = input.dataset.variantId;
          if (previousSelections.has(variantId)) {
            input.checked = true;
            this.selectedUpsells.set(variantId, previousSelections.get(variantId));
            console.log('✅ Restored selection for variant:', variantId);
          }
        });
        
        console.log('✅ Widget rebuilt with', products.length, 'products,', this.selectedUpsells.size, 'selections restored');
        
        // Update button text after rebuild to reflect restored selections
        this.updateAddToCartButtonText();
      }
      
      /**
       * Format money (simple fallback)
       */
      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents);
        }
        return '$' + (cents / 100).toFixed(2);
      }
      
      /**
       * Listen for main product form submission
       */
      initFormSubmitListener() {
        if (!this.productForm) {
          console.warn('🎁 No product form found, cannot attach submit listener');
          return;
        }
        
        const form = this.productForm.querySelector('form');
        if (!form) {
          console.warn('🎁 No form element found in product-form');
          return;
        }
        
        // Check if listener already attached (prevent double-attach)
        if (form.dataset.upsellListenerAttached) {
          console.log('🎁 Form listener already attached, skipping');
          return;
        }
        
        console.log('🎁 Attaching submit listener to form');
        
        // Check if this is a car kit builder form
        const formId = form.getAttribute('id') || '';
        const isCarKitForm = formId.includes('ckb-form-animated');
        if (isCarKitForm) {
          console.log('🚗 Car kit builder form detected');
        }
        
        // Mark as attached
        form.dataset.upsellListenerAttached = 'true';
        
        // Intercept form submission to add upsells
        // Use capture phase to intercept before product-form element
        form.addEventListener('submit', async (e) => {
          console.log('🎁 Form submit intercepted, selected upsells:', this.selectedUpsells.size, 'selected accessories:', this.selectedAccessories.size);
          
          if (this.selectedUpsells.size === 0 && this.selectedAccessories.size === 0) {
            // No extras selected, let normal form submission happen
            console.log('🎁 No extras selected, allowing normal submission');
            return;
          }
          
          // If this is a car kit form, validate car kit selections first
          if (isCarKitForm) {
            console.log('🚗 Validating car kit selections before bundle submission');
            
            // Get the variant ID field to check if it's set
            const variantIdField = form.querySelector('input[name="id"]');
            
            if (!variantIdField || !variantIdField.value) {
              console.error('❌ Car kit variant ID not set - selections incomplete');
              e.preventDefault();
              e.stopImmediatePropagation();
              alert('Please complete all car kit selections before adding to cart.');
              return;
            }
            
            console.log('✅ Car kit variant ID found:', variantIdField.value, '- proceeding with bundle');
          }
          
          // Prevent default and handle bundle submission
          console.log('🎁 Preventing default and handling bundle');
          e.preventDefault();
          e.stopImmediatePropagation();
          
          await this.handleBundleSubmit(e);
        }, true); // Use capture phase
      }
      
      /**
       * Fetch and update cart sections with retry logic for discount calculation
       */
      async fetchAndUpdateSections(sectionsToRender, cartData, retryCount = 0) {
        const maxRetries = 3;
        const retryDelay = 300; // Start with 300ms, increase on retry
        
        try {
          // Separate cart drawer section from other sections
          // Cart drawer must be fetched from /cart to ensure discounts are calculated
          const cartDrawerSection = sectionsToRender.find(s => 
            s.section === this.cartDrawer?.dataset.section && this.cartDrawer
          );
          
          const otherSections = sectionsToRender.filter(s => 
            s.section !== this.cartDrawer?.dataset.section || !this.cartDrawer
          );
          
          // Fetch cart drawer from /cart to get proper discount calculations
          if (cartDrawerSection && this.cartDrawer) {
            // Add cache-busting timestamp
            const timestamp = Date.now();
            const cartDrawerUrl = `/cart?section_id=${cartDrawerSection.section}&t=${timestamp}`;
            console.log('📄 Fetching cart drawer from /cart:', cartDrawerUrl, `(attempt ${retryCount + 1})`);
            
            try {
              const drawerResponse = await fetch(cartDrawerUrl, {
                cache: 'no-store'
              });
              const drawerHtml = await drawerResponse.text();
              
              // Parse the response
              const parser = new DOMParser();
              const doc = parser.parseFromString(drawerHtml, 'text/html');
              const newDrawerDetails = doc.querySelector('#Cart-Drawer-Details');
              const existingDrawerDetails = document.querySelector('#Cart-Drawer-Details');
              
              if (newDrawerDetails && existingDrawerDetails) {
                // Check if discounts are present in the HTML (verify discount calculation happened)
                const hasDiscounts = newDrawerDetails.querySelector('.discounted-prices, .line-discounts, .footer-discount');
                
                // Check cart data for discount indicators
                let shouldHaveDiscounts = false;
                let hasDiscountInTotal = false;
                let specificItemHasDiscount = false;
                
                if (cartData && cartData.items && cartData.items.length > 0) {
                  // Check if any items have discount allocations
                  shouldHaveDiscounts = cartData.items.some(item => 
                    item.line_level_discount_allocations?.length > 0
                  );
                  
                  // Check specifically for item 52051695239532
                  const specificItem = cartData.items.find(item => item.variant_id == 52051695239532);
                  if (specificItem) {
                    specificItemHasDiscount = specificItem.line_level_discount_allocations?.length > 0 || 
                                             (specificItem.final_line_price < specificItem.line_price) ||
                                             (specificItem.final_price < specificItem.price);
                    console.log('🎯 Specific item discount check:', {
                      variantId: specificItem.variant_id,
                      hasLineDiscounts: specificItem.line_level_discount_allocations?.length > 0,
                      priceDiff: specificItem.line_price - specificItem.final_line_price,
                      shouldHaveDiscount: specificItemHasDiscount
                    });
                  }
                  
                  // Check for cart-level discounts
                  const hasCartLevelDiscounts = cartData.cart_level_discount_applications?.length > 0;
                  
                  // Check if cart total suggests discounts should be present
                  hasDiscountInTotal = cartData.total_discount && cartData.total_discount > 0;
                  
                  // Check HTML for line-discounts on the specific item
                  // The variant ID is on the remove button, so find it and check its parent row
                  const specificItemRemoveButton = newDrawerDetails.querySelector(`button[data-variant-id="52051695239532"]`);
                  const specificItemRow = specificItemRemoveButton?.closest('tr, .cart-item');
                  const hasSpecificItemDiscountInHTML = specificItemRow?.querySelector('.line-discounts');
                  
                  // Log discount detection for debugging
                  console.log('🔍 Discount detection:', {
                    hasDiscountsInHTML: !!hasDiscounts,
                    hasSpecificItemDiscountInHTML: !!hasSpecificItemDiscountInHTML,
                    shouldHaveDiscounts,
                    specificItemHasDiscount,
                    hasCartLevelDiscounts,
                    hasDiscountInTotal,
                    totalDiscount: cartData.total_discount,
                    items: cartData.items.map(item => ({
                      id: item.variant_id,
                      lineDiscounts: item.line_level_discount_allocations?.length || 0,
                      priceDiff: item.line_price - item.final_line_price
                    }))
                  });
                  
                  // Retry if specific item should have discount but doesn't show in HTML
                  if (retryCount < maxRetries && specificItemHasDiscount && !hasSpecificItemDiscountInHTML) {
                    console.log(`⏳ Specific item discount expected but not found in HTML, retrying in ${retryDelay * (retryCount + 1)}ms...`);
                    await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
                    return this.fetchAndUpdateSections(sectionsToRender, cartData, retryCount + 1);
                  }
                  
                  // Only retry if we have clear evidence discounts should exist but don't
                  // Don't retry if discounts might not apply (e.g., discount rules not met)
                  if (retryCount < maxRetries && (shouldHaveDiscounts || hasCartLevelDiscounts || hasDiscountInTotal) && !hasDiscounts) {
                    console.log(`⏳ Discounts expected but not found in HTML, retrying in ${retryDelay * (retryCount + 1)}ms...`);
                    await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
                    return this.fetchAndUpdateSections(sectionsToRender, cartData, retryCount + 1);
                  }
                }
                
                existingDrawerDetails.innerHTML = newDrawerDetails.innerHTML;
                console.log('✅ Cart drawer updated with discounts from /cart', hasDiscounts ? '(discounts found)' : '(no discounts)');
              } else {
                console.warn('⚠️ Could not find cart drawer details in response');
                // Retry if we haven't exceeded max retries
                if (retryCount < maxRetries) {
                  await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
                  return this.fetchAndUpdateSections(sectionsToRender, cartData, retryCount + 1);
                }
              }
            } catch (drawerError) {
              console.error('❌ Error fetching cart drawer from /cart:', drawerError);
              // Retry on error if we haven't exceeded max retries
              if (retryCount < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
                return this.fetchAndUpdateSections(sectionsToRender, cartData, retryCount + 1);
              }
            }
          }
          
          // Fetch other sections (like cart-icon-bubble) from current page with cache-busting
          if (otherSections.length > 0) {
            const sectionsToFetch = otherSections.map(section => section.section).join(',');
            const timestamp = Date.now();
            const sectionsUrl = `${window.location.pathname}?sections=${sectionsToFetch}&t=${timestamp}`;
            
            console.log('📄 Fetching other sections:', sectionsUrl);
            console.log('📋 Sections to render:', otherSections);
            
            const response = await fetch(sectionsUrl, {
              cache: 'no-store'
            });
          const sections = await response.json();
          
          console.log('📦 Sections response keys:', Object.keys(sections));
          
          // Update sections - use SectionDynamicUpdate if available, otherwise fallback
          if (window.SectionDynamicUpdate) {
            console.log('🔄 Using SectionDynamicUpdate');
              window.SectionDynamicUpdate.updateSections(otherSections, sections);
          } else {
            console.log('🔄 Using fallback section update');
              this.updateSectionsManually(otherSections, sections);
            }
          }
          
          // Update cart count - use existing function if available, otherwise fallback
          if (cartData && cartData.item_count !== undefined) {
            if (window.updateCartCountInHeader) {
              window.updateCartCountInHeader(cartData.item_count);
            } else {
              this.updateCartCountManually(cartData.item_count);
            }
            console.log('✅ Cart count updated to:', cartData.item_count);
          }
          
        } catch (error) {
          console.error('❌ Error fetching sections:', error);
          // Retry on error if we haven't exceeded max retries
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
            return this.fetchAndUpdateSections(sectionsToRender, cartData, retryCount + 1);
          }
        }
      }
      
      /**
       * Manually update sections (fallback if SectionDynamicUpdate not available)
       */
      updateSectionsManually(sectionsToRender, sections) {
        sectionsToRender.forEach(sectionConfig => {
          const sectionElement = document.getElementById(sectionConfig.id);
          if (!sectionElement) {
            console.warn(`Section ${sectionConfig.id} not found`);
            return;
          }
          
          const elementToReplace = sectionConfig.selector 
            ? sectionElement.querySelector(sectionConfig.selector) 
            : sectionElement;
          
          if (elementToReplace && sections[sectionConfig.section]) {
            const html = sections[sectionConfig.section];
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newContent = doc.querySelector(sectionConfig.selector) || doc.body.firstElementChild;
            
            if (newContent) {
              elementToReplace.innerHTML = newContent.innerHTML;
              console.log(`✅ Updated ${sectionConfig.id}`);
            }
          }
        });
      }
      
      /**
       * Manually update cart count (fallback if updateCartCountInHeader not available)
       */
      updateCartCountManually(itemCount) {
        document.querySelectorAll('.cart-count-bubble').forEach(bubble => {
          // Update the visible number
          const visible = bubble.querySelector('span[aria-hidden="true"]');
          if (visible) {
            visible.textContent = itemCount;
          }
          // Update the screen-reader text
          const sr = bubble.querySelector('.visually-hidden');
          if (sr) {
            sr.textContent = itemCount === 1 ? '1 item' : itemCount + ' items';
          }
        });
      }
      
      /**
       * Get sections to render after cart update
       */
      getSectionsToRender() {
        const sections = [
          {
            id: 'Cart-Icon-Bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section'
          }
        ];
        
        if (this.cartDrawer && !this.cartDrawer.hasAttribute('data-status-silence')) {
          // Get the actual section wrapper ID
          const cartDrawerSection = this.cartDrawer.closest('.shopify-section');
          const cartDrawerSectionId = cartDrawerSection ? cartDrawerSection.id : 'Cart-Drawer';
          
          sections.push({
            id: cartDrawerSectionId,
            section: this.cartDrawer.dataset.section,
            selector: '#Cart-Drawer-Details'
          });
        }
        
        return sections;
      }
      
      /**
       * Handle bundle submission (main product + upsells)
       */
      async handleBundleSubmit(event) {
        try {
          // Show loading state
          this.setLoadingState(true);
          
          // Get main product form data
          const formData = new FormData(event.target);
          const mainVariantId = formData.get('id');
          const mainQuantity = formData.get('quantity') || this.getPrimaryQty() || 1;
          
          // Extract all properties from form data (important for OEM products)
          const properties = {};
          for (const [key, value] of formData.entries()) {
            // Properties fields are named like "properties[Color Name]"
            const propertyMatch = key.match(/^properties\[(.+)\]$/);
            if (propertyMatch) {
              properties[propertyMatch[1]] = value;
            }
          }
          
          // Build items array for cart API
          const items = [
            {
              id: mainVariantId,
              quantity: parseInt(mainQuantity),
              // Only include properties if we have any (for OEM products)
              ...(Object.keys(properties).length > 0 && { properties })
            }
          ];
          
          console.log('🎨 Main product properties:', properties);
          
          // Add selected upsells
          this.selectedUpsells.forEach(upsell => {
            items.push({
              id: upsell.variantId,
              quantity: 1
            });
          });

          // Add selected accessories (avoid duplicates)
          const existingIds = new Set(items.map(i => String(i.id)));
          this.selectedAccessories.forEach((variantId) => {
            const id = String(variantId);
            if (existingIds.has(id)) return;
            existingIds.add(id);
            items.push({ id, quantity: 1 });
          });
          
          console.log('Adding bundle to cart:', items);
          
          // Step 1: Add items sequentially to allow Shopify to calculate discounts properly
          // Adding all at once can prevent discount calculation, so we add one at a time
          let lastError = null;
          
          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            console.log(`📦 Adding item ${i + 1}/${items.length}:`, item);
            
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'Cache-Control': 'no-cache'
            },
              cache: 'no-store',
              body: JSON.stringify(item)
          });
          
          const result = await response.json();
          
          if (result.status || result.description) {
              lastError = result.description || result.message || 'Unable to add item to cart';
              console.error(`❌ Error adding item ${i + 1}:`, lastError);
              // Continue adding other items even if one fails
            } else {
              console.log(`✅ Item ${i + 1} added successfully`);
            }
            
            // Small delay between items to allow discount calculation
            if (i < items.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 150));
            }
          }
          
          // Check if we had any errors
          if (lastError && items.length === 1) {
            // Only fail if it was a single item or all items failed
            this.handleError(lastError);
            return;
          }
          
          // Success - items were added
          console.log('✅ Bundle added successfully');
          
          // Clear selections
          this.selectedUpsells.clear();
          this.toggleInputs.forEach(input => {
            input.checked = false;
          });

          this.selectedAccessories.clear();
          document.dispatchEvent(new CustomEvent('dyc:accessories-clear'));
          
          // Restore original button text after successful submission
          this.updateAddToCartButtonText();
          
          // Step 2: Wait longer for Shopify to recalculate discounts for all items
          // Sequential addition means discounts need time to propagate to all items
          // When items are added individually (not bundled), discounts calculate immediately
          // But when bundled sequentially, we need extra time for Shopify to process
          await new Promise(resolve => setTimeout(resolve, 800));
          
          // Step 3: Fetch cart.js with cache-busting to get full cart state
          let cartTimestamp = Date.now();
          let cartResponse = await fetch(`/cart.js?t=${cartTimestamp}`, {
            cache: 'no-store'
          });
          let cartData = await cartResponse.json();
          
          // Step 3b: Verify discounts are calculated, retry if needed
          // Check if the expected item has its discount calculated
          const expectedItem = cartData.items?.find(item => item.variant_id == 52051695239532);
          const expectedItemHasDiscount = expectedItem && (
            expectedItem.line_level_discount_allocations?.length > 0 ||
            expectedItem.final_line_price < expectedItem.line_price ||
            expectedItem.final_price < expectedItem.price
          );
          
          // If we expect a discount but it's not there yet, wait and retry
          if (!expectedItemHasDiscount && expectedItem) {
            console.log('⏳ Expected discount not yet calculated, waiting and retrying cart fetch...');
            await new Promise(resolve => setTimeout(resolve, 500));
            
            cartTimestamp = Date.now();
            cartResponse = await fetch(`/cart.js?t=${cartTimestamp}`, {
              cache: 'no-store'
            });
            cartData = await cartResponse.json();
            
            // Check again
            const retryExpectedItem = cartData.items?.find(item => item.variant_id == 52051695239532);
            const retryHasDiscount = retryExpectedItem && (
              retryExpectedItem.line_level_discount_allocations?.length > 0 ||
              retryExpectedItem.final_line_price < retryExpectedItem.line_price ||
              retryExpectedItem.final_price < retryExpectedItem.price
            );
            
            if (retryHasDiscount) {
              console.log('✅ Discount calculated on retry');
            } else {
              console.warn('⚠️ Discount still not calculated after retry - may need Shopify discount configuration');
            }
          }
          console.log('📦 Full cart data:', cartData);
          console.log('🔍 Cart discount analysis:', {
            totalDiscount: cartData.total_discount,
            totalPrice: cartData.total_price,
            itemCount: cartData.item_count,
            itemsWithDiscounts: cartData.items?.filter(item => 
              item.line_level_discount_allocations?.length > 0
            ).length || 0,
            cartLevelDiscounts: cartData.cart_level_discount_applications?.length || 0,
            items: cartData.items?.map(item => ({
              variantId: item.variant_id,
              productId: item.product_id,
              price: item.price,
              finalPrice: item.final_price,
              originalPrice: item.original_price,
              linePrice: item.line_price,
              finalLinePrice: item.final_line_price,
              hasLineDiscounts: item.line_level_discount_allocations?.length > 0,
              lineDiscounts: item.line_level_discount_allocations || [],
              shouldHaveDiscount: item.final_line_price < item.line_price || item.final_price < item.price
            })) || []
          });
          
          // Check specifically for the expected item with discount (reuse existing variable)
          if (expectedItem) {
            console.log('🎯 Expected item (52051695239532) discount check:', {
              variantId: expectedItem.variant_id,
              productId: expectedItem.product_id,
              price: expectedItem.price,
              finalPrice: expectedItem.final_price,
              linePrice: expectedItem.line_price,
              finalLinePrice: expectedItem.final_line_price,
              hasDiscount: expectedItem.final_price < expectedItem.price || expectedItem.final_line_price < expectedItem.line_price,
              lineDiscountAllocations: expectedItem.line_level_discount_allocations || [],
              discountAmount: expectedItem.line_price - expectedItem.final_line_price
            });
          }
          
          // Step 4: Fetch updated sections (with retry logic for discounts)
          // These UI updates should NOT block cart success - wrap in try/catch
          try {
            const sectionsToRender = this.getSectionsToRender();
            await this.fetchAndUpdateSections(sectionsToRender, cartData, 0);
          } catch (uiError) {
            console.warn('🎁 UI section update failed (non-fatal):', uiError);
          }
          
          // Step 5: Trigger PUB_SUB_EVENTS with full cart data
          try {
            if (window.publish && window.PUB_SUB_EVENTS) {
              console.log('📢 Publishing cartUpdate event');
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                source: 'upsell-widget',
                productVariantId: mainVariantId,
                cartData: cartData // Use full cart data, not just added items
              });
            }
          } catch (pubsubError) {
            console.warn('🎁 PubSub publish failed (non-fatal):', pubsubError);
          }
          
          // Step 6: Open cart drawer AFTER everything is updated
          console.log('Opening cart drawer...');
          try {
            if (this.cartDrawer && !this.cartDrawer.hasAttribute('data-status-silence')) {
              // Small delay to ensure DOM is updated
              setTimeout(() => {
                try {
                  this.cartDrawer.show(event.submitter);
                } catch (showError) {
                  console.warn('🎁 cartDrawer.show() failed, trying fallback:', showError);
                  // Fallback: try without submitter
                  try {
                    this.cartDrawer.show();
                  } catch (e) {
                    // Last resort: click cart toggle
                    const cartToggle = document.querySelector('[aria-controls="Cart-Drawer"]');
                    if (cartToggle) cartToggle.click();
                  }
                }
              }, 100);
            } else {
              window.location = window['routes']['cart_url'];
            }
          } catch (drawerError) {
            console.warn('🎁 Cart drawer open failed (non-fatal):', drawerError);
            // Fallback: click cart toggle
            const cartToggle = document.querySelector('[aria-controls="Cart-Drawer"]');
            if (cartToggle) cartToggle.click();
          }
          
        } catch (error) {
          // This catch is now only for actual cart add failures
          console.error('Error adding bundle to cart:', error);
          // Don't show error if items were actually added - just open cart
          const cartToggle = document.querySelector('[aria-controls="Cart-Drawer"]');
          if (cartToggle) {
            console.log('🎁 Fallback: clicking cart toggle after error');
            cartToggle.click();
          } else {
            this.handleError('An error occurred. Please try again.');
          }
        } finally {
          this.setLoadingState(false);
        }
      }
      
      /**
       * Set loading state
       */
      setLoadingState(isLoading) {
        if (isLoading) {
          this.classList.add('loading');
          this.toggleInputs.forEach(input => input.disabled = true);
          
          // Also disable main form buttons
          if (this.productForm) {
            const buttons = this.productForm.querySelectorAll('button[type="submit"]');
            buttons.forEach(btn => {
              btn.classList.add('loading');
              btn.disabled = true;
            });
          }
        } else {
          this.classList.remove('loading');
          this.toggleInputs.forEach(input => input.disabled = false);
          
          // Re-enable main form buttons
          if (this.productForm) {
            const buttons = this.productForm.querySelectorAll('button[type="submit"]');
            buttons.forEach(btn => {
              btn.classList.remove('loading');
              btn.disabled = false;
            });
          }
        }
      }
      
      
      /**
       * Handle error
       */
      handleError(message) {
        console.error('Cart error:', message);
        
        // Show error message
        const errorWrapper = document.getElementById(`Product-Form-Error-Message-${this.sectionId}`);
        if (errorWrapper) {
          errorWrapper.textContent = message;
          errorWrapper.removeAttribute('hidden');
          errorWrapper.classList.remove('hidden');
          
          // Auto-hide after 5 seconds
          setTimeout(() => {
            errorWrapper.setAttribute('hidden', '');
            errorWrapper.classList.add('hidden');
          }, 5000);
        } else {
          // Fallback to alert
          console.warn('Error wrapper not found, using alert');
          alert(message);
        }
      }
      
      /**
       * Update sections after cart add
       */
      updateSections(sections) {
        Object.keys(sections).forEach(sectionId => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.innerHTML = sections[sectionId];
          }
        });
      }
    }
  );
}

})();